// src/Pages/Split/BlitzSplit.jsx — v8.1
//
// ═══════════════════════════════════════════════════════════════════════
// BLITZ SPLIT — invoice a PERSON at a BlitzPay company, with your
// contractors on it.
//
// v8.1 CHANGES (from v8)
// ----------------------
//   · CONTACT PICKER IS A DIALOG — picking a company immediately pops
//     "Who gets this invoice?" as a focused popup (fullScreen on mobile).
//     One decision at a time instead of a stack of inline choices.
//   · SOLID CARD — the split card is fully opaque on top of the globe.
//     No more translucent/backdrop-blur treatment; the globe stays as
//     ambient background only.
//   · BilledToStrip: tapping the person reopens the dialog; the pencil
//     reopens company search.
//
// THE MODEL (unchanged)
//   [ just you ]            → standard invoice  (invoices.create)
//   [ contractors on it ]   → Split             (bbc.create — same rails)
//
// Yellow discipline: ink-on-yellow everywhere. Never white on yellow.
// ═══════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box, Typography, TextField, Button, Paper, Avatar, IconButton,
  InputAdornment, CircularProgress, Collapse, Alert, Snackbar, Stack,
  Switch,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  Refresh as RefreshIcon, Send as SendIcon, ExpandMore, ExpandLess,
  Bolt, Upload as UploadIcon, TableChart as CSVIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import client from "../../API";
import useAuth from "../../Hooks/use-auth";
import { getPONumber, parseLocalFloat } from "../../Utils/constants";
import { showSuccessToast, handleApiError } from "../../Utils/toastNotifications";

import {
  SPLIT_YELLOW, SPLIT_YELLOW_DARK, SPLIT_YELLOW_DEEP, SPLIT_INK,
  SPLIT_TINT, SPLIT_BORDER, TERMS, fadeUp, fmt,
} from "./BlitzSplit/SplitTokens";
import {
  makeRow, nameFromEmail, parseCSV, isEmailish,
  presetKeyFor, presetKeyForCreator, presetKeyForEmail,
  loadLocalPresets, persistLocalPresets, fetchServerPresets, syncPresetToServer,
} from "./BlitzSplit/SplitData";
import { BlitzPayCompanyField, ContactPickerDialog, BilledToStrip } from "./BlitzSplit/SplitPayer";
import {
  MemberBubble, AddBubble, GhostMeBubble, UnifiedSearch, AmountCard,
} from "./BlitzSplit/SplitMembers";
import { GlobeBackground, SplitBar, DeferredBar, SplitHero } from "./BlitzSplit/SplitVisuals";

const BlitzSplit = () => {
  const navigate = useNavigate();
  const { getCurrrentUser } = useAuth();
  const currentUser = getCurrrentUser();
  const csvInputRef = useRef(null);

  // The four things a Split is
  const [rows, setRows] = useState([]);                     // who's on it
  const [payerCompany, setPayerCompany] = useState(null);   // which company
  const [billedContact, setBilledContact] = useState(null); // WHO gets it
  const [paymentTerm, setPaymentTerm] = useState(30);       // when

  // v8.1: the contact decision is a popup
  const [contactDialogOpen, setContactDialogOpen] = useState(false);

  // Deferred distribution — bill one total, assign shares later
  const [deferDistribution, setDeferDistribution] = useState(false);
  const [deferTotal, setDeferTotal] = useState("");

  // Contractor presets — { [key]: { amount, blitzpayPre, savedAt } }
  const [presets, setPresets] = useState({});

  // Supporting
  const [creatorList, setCreatorList] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [payerEditing, setPayerEditing] = useState(true);
  const [poNumber, setPoNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snack, setSnack] = useState(null);

  useEffect(() => {
    setPoNumber(getPONumber("SPLIT"));
    client.creators.list()
      .then((data) => setCreatorList((data || []).map((c) => ({ ...c, label: c.creator }))))
      .catch(() => setSnack({ severity: "error", message: "Couldn't load your creators — refresh to retry." }));

    const local = loadLocalPresets();
    setPresets(local);
    fetchServerPresets().then((remote) => {
      if (remote) {
        const merged = { ...local, ...remote };
        setPresets(merged);
        persistLocalPresets(merged);
      }
    });
  }, []);

  // v8.1: picking a company resets the contact and pops the dialog.
  useEffect(() => {
    setBilledContact(null);
    if (payerCompany) setContactDialogOpen(true);
  }, [payerCompany]);

  const handleContactPick = (contact) => {
    setBilledContact(contact);
    setContactDialogOpen(false);
    setPayerEditing(false);   // company + person locked in → collapse search
  };

  // ── Preset operations ─────────────────────────────────────────────────
  const savePresetForRow = (row) => {
    const key = presetKeyFor(row);
    if (!key) return;
    setPresets((prev) => {
      const next = { ...prev };
      if (next[key]) {
        delete next[key];
        syncPresetToServer(key, null);
        setSnack({ severity: "info", message: `Default cleared for ${row.name}.` });
      } else {
        const preset = {
          amount: row.amount && parseLocalFloat(row.amount) > 0 ? String(parseLocalFloat(row.amount)) : "",
          blitzpayPre: !!row.blitzpayPre,
          savedAt: Date.now(),
        };
        next[key] = preset;
        syncPresetToServer(key, preset);
        setSnack({
          severity: "success",
          message: `Saved default for ${row.name}${preset.amount ? ` — ${fmt(parseLocalFloat(preset.amount))}` : ""}${preset.blitzpayPre ? " · ⚡ BlitzPay" : ""}. Applies every time you add them.`,
        });
      }
      persistLocalPresets(next);
      return next;
    });
  };

  // ── Row operations ────────────────────────────────────────────────────
  const hasMe = rows.some((r) => r.kind === "me");
  const addMe = () => !hasMe && setRows((r) => [
    ...r,
    makeRow("me", { name: currentUser?.first_name ? `${currentUser.first_name} ${currentUser.last_name || ""}`.trim() : "You" }),
  ]);
  const addCreator = (creator) => {
    if (!creator || rows.some((r) => r.creator?.creator === creator.creator)) return;
    const preset = presets[presetKeyForCreator(creator)];
    setRows((r) => [...r, makeRow("creator", {
      creator, name: creator.creator,
      ...(preset ? { amount: preset.amount || "", blitzpayPre: !!preset.blitzpayPre, presetApplied: true } : {}),
    })]);
  };
  const addInvite = ({ email, amount = "" }) => {
    const cleanEmail = (email || "").trim();
    if (!isEmailish(cleanEmail)) return;
    const preset = presets[presetKeyForEmail(cleanEmail)];
    setRows((r) => [...r, makeRow("invite", {
      name: nameFromEmail(cleanEmail),
      email: cleanEmail,
      amount: amount || preset?.amount || "",
      ...(preset ? { blitzpayPre: !!preset.blitzpayPre, presetApplied: true } : {}),
    })]);
  };
  const updateRow = (rowId, patch) =>
    setRows((r) => r.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));
  const setAmount = (rowId, amount) => updateRow(rowId, { amount, presetApplied: false });
  const togglePre = (rowId) =>
    setRows((r) => r.map((row) => (row.rowId === rowId ? { ...row, blitzpayPre: !row.blitzpayPre } : row)));
  const removeRow = (rowId) => setRows((r) => r.filter((row) => row.rowId !== rowId));

  // ── CSV import — email + amount only; presets fill gaps (CSV wins) ────
  const handleCSV = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const parsed = parseCSV(String(e.target.result || ""));
      if (!parsed.length) {
        setSnack({ severity: "warning", message: "Couldn't read any rows — expected columns: email, amount. Every row needs a valid email." });
        return;
      }
      const existingEmails = new Set(rows.filter((r) => r.email).map((r) => r.email.toLowerCase()));
      let added = 0, skipped = 0;
      const next = [];
      parsed.forEach((p) => {
        const lowerEmail = p.email.toLowerCase();
        if (existingEmails.has(lowerEmail)) { skipped++; return; }
        const match = creatorList.find((c) => c.email && c.email.toLowerCase() === lowerEmail);
        const preset = match
          ? presets[presetKeyForCreator(match)]
          : presets[presetKeyForEmail(p.email)];
        const amount = p.amount || preset?.amount || "";
        next.push(match
          ? makeRow("creator", { creator: match, name: match.creator, amount,
              ...(preset ? { blitzpayPre: !!preset.blitzpayPre, presetApplied: !p.amount && !!preset.amount } : {}) })
          : makeRow("invite", { name: nameFromEmail(p.email), email: p.email, amount,
              ...(preset ? { blitzpayPre: !!preset.blitzpayPre, presetApplied: !p.amount && !!preset.amount } : {}) }));
        existingEmails.add(lowerEmail);
        added++;
      });
      setRows((r) => [...r, ...next]);
      setSnack({
        severity: "success",
        message: `Imported ${added} recipient${added !== 1 ? "s" : ""}${skipped ? ` · ${skipped} duplicate${skipped !== 1 ? "s" : ""} skipped` : ""}.`,
      });
    };
    reader.readAsText(file);
  };

  // ── Derived ───────────────────────────────────────────────────────────
  const { rowSum, contractorRows, myRow, route, preCount } = useMemo(() => {
    const sum = rows.reduce((acc, r) => acc + (parseLocalFloat(r.amount) || 0), 0);
    const contractors = rows.filter((r) => r.kind !== "me");
    const me = rows.find((r) => r.kind === "me") || null;
    const pre = contractors.filter((r) => r.blitzpayPre).length;
    let derived = null;
    if (rows.length >= 1 && contractors.length >= 1) derived = "split";
    else if (rows.length === 1 && me) derived = "self_invoice";
    return { rowSum: sum, contractorRows: contractors, myRow: me, route: derived, preCount: pre };
  }, [rows]);

  const total = deferDistribution ? (parseLocalFloat(deferTotal) || 0) : rowSum;

  const pctOf = (amount) => {
    const amt = parseLocalFloat(amount) || 0;
    if (!total || !amt) return null;
    const pct = (amt / total) * 100;
    return pct % 1 ? pct.toFixed(1) : pct.toFixed(0);
  };

  // ── Validation — a Split needs a person, not just a company ───────────
  const rowsValid = rows.length > 0 && rows.every((r) => {
    const identityOk = r.kind !== "invite" || ((r.email.trim() || "").includes("@") && r.name.trim());
    if (deferDistribution) return identityOk;
    return identityOk && parseLocalFloat(r.amount) > 0;
  });
  const totalValid = deferDistribution ? parseLocalFloat(deferTotal) > 0 : rowSum > 0;
  const canSend = rowsValid && totalValid && !!payerCompany && !!billedContact && !submitting;

  // ── Submit — addressed to the person; roster goes price-less ──────────
  const handleSend = async () => {
    setSubmitting(true);
    try {
      if (route === "split") {
        await client.bbc.create({
          po_number: poNumber,
          enterprise_company_id: payerCompany.id,
          enterprise_user_id: billedContact.kind === "user" ? billedContact.id : null,
          enterprise_email: billedContact.email,
          enterprise_name: billedContact.name,
          payment_term: paymentTerm,
          notes: notes || undefined,
          invoice_total: total,
          ...(deferDistribution ? { distribution_pending: true } : {}),
          recipients: contractorRows.map((r) => ({
            creator_id: r.kind === "creator" ? r.creator?.creator : undefined,
            email: r.kind === "invite" ? r.email.trim() : undefined,
            name: r.name.trim() || undefined,
            amount: deferDistribution ? null : parseLocalFloat(r.amount),
            ...(r.blitzpayPre && !deferDistribution ? { blitzpay_preapproved: true } : {}),
          })),
        });
        showSuccessToast("Split sent", {
          description: deferDistribution
            ? `${billedContact.name} at ${payerCompany.name} has the invoice. Set everyone's share whenever you're ready — it applies when the transfer lands.`
            : preCount > 0
              ? `${billedContact.name} at ${payerCompany.name} has the invoice. ${preCount} payout${preCount !== 1 ? "s" : ""} pre-accepted via BlitzPay — the rest apply at settlement.`
              : `${billedContact.name} at ${payerCompany.name} has the invoice. Contractors see their share now — it applies when the transfer lands.`,
        });
      } else {
        await client.invoices.create({
          creator_id: null,
          po_number: poNumber,
          amount_due: total,
          email: billedContact.email,
          client_name: billedContact.name,
          client_email: billedContact.email,
          client_notes: notes,
          payment_term: paymentTerm,
          preferred_settlement_method: "balance",
          allow_balance_payment: true,
          items: [{ description: notes || "Services", amount: total }],
        });
        showSuccessToast("Invoice sent", {
          description: `${billedContact.name} at ${payerCompany.name} has the invoice.`,
        });
      }
      setTimeout(() => navigate("/blitzpay"), 1200);
    } catch (err) {
      handleApiError(err, "Couldn't send the Split");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ width: "100%", p: { xs: 2, md: 3 }, pb: 10, position: "relative", minHeight: "100vh" }}>
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, display: "flex",
        alignItems: "center", justifyContent: "center", pointerEvents: "none", zIndex: 0 }}>
        <GlobeBackground size={1200} />
      </Box>

      <input ref={csvInputRef} type="file" accept=".csv,text/csv" hidden
        onChange={(e) => { handleCSV(e.target.files?.[0]); e.target.value = ""; }} />

      {/* The contact decision — a focused popup, not an inline list */}
      <ContactPickerDialog
        open={contactDialogOpen}
        company={payerCompany}
        value={billedContact}
        onPick={handleContactPick}
        onClose={() => setContactDialogOpen(false)}
      />

      <Box sx={{ maxWidth: 680, mx: "auto", position: "relative", zIndex: 1 }}>

        <SplitHero total={total} payer={payerCompany} contact={billedContact}
          peopleCount={rows.length} deferred={deferDistribution} />

        {/* Company search — collapses once company + person are locked in */}
        <Collapse in={payerEditing || !payerCompany}>
          <Box sx={{ mb: 2 }}>
            <BlitzPayCompanyField value={payerCompany} onChange={setPayerCompany} />
          </Box>
        </Collapse>

        {/* ── THE SPLIT CARD — fully opaque on top of the globe ─────────── */}
        <Paper elevation={2} sx={{ border: "1px solid", borderColor: "grey.200", borderRadius: 3.5,
          overflow: "hidden", mb: 2.5, position: "relative",
          bgcolor: "background.paper" }}>
          <Box sx={{ height: 4, background: `linear-gradient(90deg, ${SPLIT_YELLOW}, ${alpha(SPLIT_YELLOW, 0.4)})` }} />

          <BilledToStrip payer={payerCompany} contact={billedContact}
            paymentTerm={paymentTerm} onTerm={setPaymentTerm}
            onEditCompany={() => setPayerEditing(true)}
            onEditContact={() => payerCompany && setContactDialogOpen(true)} />

          <Box sx={{ p: 2.5 }}>

            {/* MEMBER STRIP — group chat style */}
            <Box sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1, pt: 0.75,
              "&::-webkit-scrollbar": { height: 4 },
              "&::-webkit-scrollbar-thumb": { bgcolor: alpha(SPLIT_YELLOW, 0.3), borderRadius: 2 } }}>
              {rows.map((row) => (
                <MemberBubble key={row.rowId} row={row} onRemove={removeRow} />
              ))}
              {!hasMe && <GhostMeBubble onClick={addMe} />}
              <AddBubble active={searchOpen} onClick={() => setSearchOpen(!searchOpen)} />
            </Box>

            {/* ONE SEARCH — opens under the [+] */}
            <Collapse in={searchOpen}>
              <Box sx={{ mt: 0.5, mb: 1.5 }}>
                <UnifiedSearch creatorList={creatorList} rows={rows} presets={presets}
                  onPickCreator={addCreator} onAddInvite={addInvite} />
              </Box>
            </Collapse>

            {/* SHARES — amounts now, or distribution later */}
            {rows.length > 0 && (
              <>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between",
                  mt: 1.5, mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: "text.disabled",
                    textTransform: "uppercase", letterSpacing: "0.08em", fontSize: "0.62rem" }}>
                    Shares{preCount > 0 && !deferDistribution ? ` · ⚡ ${preCount} pre-accepted` : ""}
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box onClick={() => setDeferDistribution(!deferDistribution)}
                      sx={{ display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer",
                        userSelect: "none" }}>
                      <Switch size="small" checked={deferDistribution}
                        sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: SPLIT_YELLOW_DARK },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SPLIT_YELLOW } }} />
                      <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.68rem",
                        color: deferDistribution ? SPLIT_YELLOW_DEEP : "text.secondary" }}>
                        Set distribution later
                      </Typography>
                    </Box>
                    {!deferDistribution && total > 0 && (
                      <Typography variant="body2" sx={{ fontWeight: 800,
                        fontVariantNumeric: "tabular-nums" }}>
                        {fmt(total)}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Collapse in={deferDistribution}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 1.5, borderRadius: 2.5,
                    borderColor: SPLIT_BORDER, bgcolor: alpha(SPLIT_YELLOW, 0.03) }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                      <Box sx={{ flex: 1, minWidth: 180 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.85rem" }}>
                          Bill the total now, divide it later
                        </Typography>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ fontSize: "0.7rem" }}>
                          The invoice goes out with everyone attached. Assign each share whenever
                          you're ready.
                        </Typography>
                      </Box>
                      <TextField size="small" type="number" placeholder="0.00" value={deferTotal}
                        onChange={(e) => setDeferTotal(e.target.value)} autoFocus
                        label="Invoice total"
                        InputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>,
                          sx: { borderRadius: 2, fontWeight: 800, "& input": { textAlign: "right" } },
                        }}
                        sx={{ width: 160, flexShrink: 0 }} />
                    </Box>
                  </Paper>
                </Collapse>

                <Stack spacing={1}>
                  {rows.map((row) => (
                    <AmountCard key={row.rowId} row={row} total={total} onAmount={setAmount}
                      onTogglePre={togglePre} onSavePreset={savePresetForRow}
                      hasPreset={!!presets[presetKeyFor(row)]}
                      deferred={deferDistribution} />
                  ))}
                </Stack>
              </>
            )}

            {rows.length === 0 && !searchOpen && (
              <Typography variant="caption" color="text.secondary"
                sx={{ display: "block", fontWeight: 600, py: 1 }}>
                Tap <Box component="span" sx={{ fontWeight: 800, color: SPLIT_YELLOW_DEEP }}>+</Box> to
                add your contractors — like adding people to a group chat.
              </Typography>
            )}

            {/* Live proportion bar — real slices, or the deferred ghost */}
            {rows.length > 0 && total > 0 && (
              <Box sx={{ mt: 2 }}>
                {deferDistribution
                  ? <DeferredBar count={rows.length} />
                  : <SplitBar rows={rows} total={total} />}
              </Box>
            )}

            {/* CSV — quiet dashed entry at the bottom of the card */}
            <Box onClick={() => csvInputRef.current?.click()} sx={{
              mt: 2, p: 1.25, borderRadius: 2, border: "1px dashed",
              borderColor: alpha(SPLIT_YELLOW, 0.25),
              cursor: "pointer", display: "flex", alignItems: "center", gap: 1,
              "&:hover": { borderColor: SPLIT_YELLOW_DARK, bgcolor: alpha(SPLIT_YELLOW, 0.02) },
              transition: "all 0.15s",
            }}>
              <CSVIcon sx={{ fontSize: 15, color: SPLIT_YELLOW_DARK }} />
              <Typography variant="caption" sx={{ fontWeight: 700, color: SPLIT_YELLOW_DEEP, flex: 1 }}>
                Adding a whole roster? Import a CSV
              </Typography>
              <Typography variant="caption" color="text.disabled" sx={{ fontSize: "0.64rem" }}>
                email · amount
              </Typography>
              <UploadIcon sx={{ fontSize: 14, color: SPLIT_YELLOW_DARK }} />
            </Box>
          </Box>
        </Paper>

        {/* Reference & notes */}
        <Button size="small" onClick={() => setDetailsOpen(!detailsOpen)}
          endIcon={detailsOpen ? <ExpandLess /> : <ExpandMore />}
          sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}>
          Reference & notes
        </Button>
        <Collapse in={detailsOpen}>
          <Stack spacing={1.5} sx={{ mt: 1.5, mb: 1 }}>
            <TextField label="PO number" size="small" value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setPoNumber(getPONumber("SPLIT"))}
                      title="Generate new">
                      <RefreshIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
            <TextField label="Notes for the payer" size="small" multiline rows={2}
              value={notes} onChange={(e) => setNotes(e.target.value)}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, bgcolor: "background.paper" } }} />
          </Stack>
        </Collapse>

        {/* What happens when you send */}
        {route && total > 0 && payerCompany && billedContact && (
          <Paper elevation={0} sx={{ mt: 1.5, p: 2.5, borderRadius: 2.5,
            bgcolor: SPLIT_TINT, border: "1px solid", borderColor: SPLIT_BORDER,
            animation: `${fadeUp} 0.25s ease` }}>
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Avatar sx={{ width: 30, height: 30, bgcolor: SPLIT_YELLOW, color: SPLIT_INK }}>
                <Bolt fontSize="small" />
              </Avatar>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {route === "split" ? (
                  deferDistribution ? (
                    <>
                      <strong>{billedContact.name}</strong> at <strong>{payerCompany.name}</strong>{" "}
                      receives one invoice for <strong>{fmt(total)}</strong> on{" "}
                      {TERMS.find((t) => t.value === paymentTerm)?.label}, listing{" "}
                      {contractorRows.length === 1 ? "your contractor" : `all ${contractorRows.length} contractors`}
                      {" "}— names only, no individual amounts. Shares aren't set yet — assign the
                      distribution whenever you're ready, and each person's cut applies when the
                      transfer lands.
                    </>
                  ) : (
                    <>
                      <strong>{billedContact.name}</strong> at <strong>{payerCompany.name}</strong>{" "}
                      receives one invoice for <strong>{fmt(total)}</strong> on{" "}
                      {TERMS.find((t) => t.value === paymentTerm)?.label}, listing{" "}
                      {contractorRows.length === 1 ? "your contractor" : `all ${contractorRows.length} contractors`}
                      {" "}— names only, no individual amounts.
                      {billedContact.kind === "user"
                        ? " They approve it in-app;"
                        : " Their AP team approves it by email;"}{" "}
                      each person's share applies when the transfer lands.
                      {preCount > 0
                        ? ` ⚡ ${preCount === contractorRows.length ? (contractorRows.length === 1 ? "Their payout is" : "All payouts are") : `${preCount} of them ${preCount === 1 ? "is" : "are"}`} pre-accepted via BlitzPay — the early payout is already elected, with the conversion fee off their slice.`
                        : " Anyone who wants theirs sooner can take an early payout on their own slice and cover the fee themselves."}
                      {myRow && parseLocalFloat(myRow.amount) > 0 &&
                        ` Your ${fmt(parseLocalFloat(myRow.amount))} (${pctOf(myRow.amount)}%) comes to you at settlement.`}
                    </>
                  )
                ) : (
                  <>
                    <strong>{billedContact.name}</strong> at <strong>{payerCompany.name}</strong>{" "}
                    receives one invoice for <strong>{fmt(total)}</strong>. The money lands in your
                    balance when they pay.
                  </>
                )}
              </Typography>
            </Stack>
          </Paper>
        )}

        {/* Send */}
        <Button variant="contained" size="large" fullWidth disabled={!canSend}
          onClick={handleSend}
          startIcon={submitting ? <CircularProgress size={18} sx={{ color: SPLIT_INK }} /> : <SendIcon />}
          sx={{
            mt: 2.5, height: 52, borderRadius: 2.5, fontWeight: 800, fontSize: "0.95rem",
            bgcolor: SPLIT_YELLOW, color: SPLIT_INK,
            boxShadow: `0 4px 18px ${alpha(SPLIT_YELLOW, 0.45)}`,
            "&:hover": { bgcolor: SPLIT_YELLOW_DARK, transform: "translateY(-1px)" },
            "&.Mui-disabled": { bgcolor: alpha(SPLIT_YELLOW, 0.3), color: alpha(SPLIT_INK, 0.4) },
            transition: "all 0.2s ease",
          }}>
          {submitting ? "Sending…"
            : !billedContact && payerCompany ? "Pick who gets the invoice"
            : `Send Split${total > 0 ? ` · ${fmt(total)}` : ""}${deferDistribution && total > 0 ? " · shares later" : ""}`}
        </Button>
      </Box>

      <Snackbar open={!!snack} autoHideDuration={6000} onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={snack?.severity || "info"} variant="filled" onClose={() => setSnack(null)}>
          {snack?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BlitzSplit;